import requests from "./httpService";
import secureLocalStorage from "react-secure-storage";

const ProjectServices = {
  getAllProjects(companyId) {
    return requests.get(`/projects?company_id=${companyId}`);
  },
  getAllProjectsForAssignment(companyId, { waitFresh } = {}) {
    return requests.get(`/projects?company_id=${companyId}&scope=all&lite=true${waitFresh ? "&wait_fresh=1" : ""}`);
  },
  getProjectsList(companyId) {
    return requests.get(`/projects/list?company_id=${companyId}`);
  },
  createProject(body) {
    return requests.post(`/projects/create`, body);
  },
  updateProject(id, body) {
    return requests.post(`/projects/update/${id}`, body);
  },
  deleteProject(id) {
    return requests.delete(`/projects/delete/${id}`);
  },
  getProjectDetail(id) {
    return requests.get(`/projects/detail/${id}`);
  },
  uploadAttachment(projectId, formData, headers) {
    return requests.post(`/projects/attachments/${projectId}`, formData, headers);
  },
  deleteAttachment(attachmentId) {
    return requests.post(`/projects/attachmentdelete/${attachmentId}`);
  },
  // AI: suggest tasks from an attached document (nothing saved), then save the approved ones.
  // The server streams one JSON event per line (stage / count / task / done / error); axios can't
  // read a response while it arrives, so this uses fetch with the same auth header as httpService.
  async aiAnalyzeDocumentStream(projectId, attachmentId, detail, onEvent, signal, fresh = false, answers = []) {
    let token;
    try { token = JSON.parse(secureLocalStorage.getItem("adminInfo"))?.authToken; } catch (e) { token = undefined; }
    const base = String(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
    const res = await fetch(`${base}/projects/ai/analyze/${projectId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", authToken: token || "" },
      body: JSON.stringify({ attachment_id: attachmentId, detail, fresh, answers }),
      signal,
    });
    if (!res.headers.get("content-type")?.includes("ndjson")) {
      const text = await res.text();
      if (res.status === 404 && text.includes("not defined on this server")) {
        throw new Error(`The backend at ${base} doesn't have the AI feature yet — deploy the latest backend there, or point REACT_APP_BASE_URL at a backend that has it.`);
      }
      let msg = "The AI could not analyze this document.";
      try { const body = JSON.parse(text); msg = body.data || body.message || msg; } catch (e) { if (text) msg = text; }
      throw new Error(`${msg} (HTTP ${res.status})`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      lines.filter(Boolean).forEach((line) => onEvent(JSON.parse(line)));
    }
    if (buffer.trim()) onEvent(JSON.parse(buffer));
  },
  // Step 1: questions the document leaves open, each with a few likely answers
  aiDocumentQuestions(projectId, attachmentId) {
    return requests.post(`/projects/ai/questions/${projectId}`, { attachment_id: attachmentId });
  },
  aiCreateTasks(projectId, tasks, companyIdentifier) {
    return requests.post(`/projects/ai/create-tasks/${projectId}`, { tasks, company_identifier: companyIdentifier });
  },
};

export default ProjectServices;
